/**
 * ConfirmDialog — reemplazo de window.confirm() / window.prompt() con un modal
 * real (Radix Dialog, Escape + focus trap gratis), consistente con el resto
 * del sistema de diseño.
 *
 * Uso básico (reemplaza confirm()):
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "¿Eliminar tarea?", description: "Esto es permanente.", variant: "destructive" })) { ... }
 *
 * Con opciones múltiples (reemplaza el prompt "1/2/3" de borrar tarea):
 *   const choice = await confirm({
 *     title: "Eliminar tarea",
 *     description: "¿Qué querés hacer con los archivos en Drive?",
 *     choices: [
 *       { key: "papelera",  label: "Mover a papelera", variant: "outline" },
 *       { key: "definitivo", label: "Borrar definitivo", variant: "destructive" },
 *     ],
 *   }); // → "papelera" | "definitivo" | null (cancelado/Escape)
 *
 * Con campo de texto (reemplaza prompt() de motivo):
 *   const res = await confirm({
 *     title: "Motivo de cancelación",
 *     input: { label: "Motivo", required: true },
 *   }); // → { value } | null
 *
 * Con campo de texto Y fecha (p. ej. pausar una tarea: motivo + hasta cuándo):
 *   const res = await confirm({
 *     title: "Pausar tarea",
 *     input: { label: "Motivo", required: true, multiline: true },
 *     fecha: { label: "Pausada hasta", required: true, min: "2026-10-12", defaultValue: "2026-10-19" },
 *   }); // → { value, fecha } | null
 */
import { createContext, useContext, useCallback, useState, useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AlertTriangle } from "../lib/icons.jsx";
import { cn } from "../lib/utils.js";
import { Button } from "./ui/button.jsx";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { opts, resolve }

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({ opts, resolve });
    });
  }, []);

  const settle = useCallback((value) => {
    setState(s => {
      s?.resolve(value);
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialogRoot state={state} onSettle={settle} />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

function ConfirmDialogRoot({ state, onSettle }) {
  const open = !!state;
  const opts = state?.opts || {};
  const [value, setValue] = useState("");
  const [fecha, setFecha] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (opts.input?.type === "select") {
      const first = opts.input.options?.[0];
      setValue(opts.input.defaultValue ?? (first?.value ?? first ?? ""));
    } else {
      setValue(opts.input?.defaultValue || "");
    }
    setFecha(opts.fecha?.defaultValue || "");
  }, [open, opts.input, opts.fecha]);

  useEffect(() => {
    if (open && opts.input) {
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [open, opts.input]);

  function nullResult() {
    return opts.choices ? null : opts.input ? null : false;
  }

  function handleOpenChange(next) {
    if (!next) onSettle(nullResult());
  }

  function handleChoice(key) {
    if (opts.input) {
      if (opts.input.required && !value.trim()) return;
      if (opts.fecha?.required && !fecha) return;
      onSettle(opts.fecha ? { value: value.trim(), fecha } : { value: value.trim() });
      return;
    }
    if (opts.choices) {
      onSettle(key);
      return;
    }
    onSettle(key === "confirm");
  }

  const destructive = opts.variant === "destructive";
  const buttons = opts.choices ?? [
    { key: "cancel",  label: opts.cancelLabel ?? "Cancelar", variant: "outline" },
    { key: "confirm", label: opts.confirmLabel ?? "Confirmar", variant: destructive ? "destructive" : "default" },
  ];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm animate-fade-in" />
        <DialogPrimitive.Content
          className="fixed z-[80] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-card rounded-3xl shadow-2xl p-5 animate-slide-up focus:outline-none"
          onEscapeKeyDown={() => onSettle(nullResult())}
          {...(opts.description ? {} : { "aria-describedby": undefined })}
        >
          <div className="flex items-start gap-3">
            {destructive && (
              <div className="mt-0.5 flex-shrink-0 h-8 w-8 rounded-full bg-[#DC2626]/10 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-[#DC2626]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <DialogPrimitive.Title className="text-sm font-semibold text-foreground">
                {opts.title}
              </DialogPrimitive.Title>
              {opts.description && (
                <DialogPrimitive.Description className="mt-1.5 text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                  {opts.description}
                </DialogPrimitive.Description>
              )}
            </div>
          </div>

          {opts.input && (
            <div className="mt-3">
              {opts.input.label && (
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  {opts.input.label}{opts.input.required && <span className="text-[#DC2626]"> *</span>}
                </label>
              )}
              {opts.input.type === "select" ? (
                <select
                  ref={inputRef}
                  className="w-full h-8 rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={value}
                  onChange={e => setValue(e.target.value)}
                >
                  {opts.input.options.map(o => (
                    <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
                  ))}
                </select>
              ) : opts.input.multiline ? (
                <textarea
                  ref={inputRef}
                  className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                  rows={3}
                  value={value}
                  placeholder={opts.input.placeholder}
                  onChange={e => setValue(e.target.value)}
                />
              ) : (
                <input
                  ref={inputRef}
                  type="text"
                  className="w-full h-8 rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={value}
                  placeholder={opts.input.placeholder}
                  onChange={e => setValue(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleChoice("confirm"); }}
                />
              )}
            </div>
          )}

          {opts.input && opts.fecha && (
            <div className="mt-3">
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                {opts.fecha.label}{opts.fecha.required && <span className="text-[#DC2626]"> *</span>}
              </label>
              <input
                type="date"
                className="w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={fecha}
                min={opts.fecha.min}
                onChange={e => setFecha(e.target.value)}
              />
            </div>
          )}

          <div className={cn("mt-4 flex gap-2", buttons.length > 2 ? "flex-col" : "justify-end")}>
            {buttons.map(b => (
              <Button
                key={b.key}
                size="sm"
                variant={b.variant ?? "outline"}
                className={buttons.length > 2 ? "w-full justify-center" : ""}
                onClick={() => handleChoice(b.key)}
                disabled={b.key !== "cancel" && ((opts.input?.required && !value.trim()) || (opts.fecha?.required && !fecha))}
              >
                {b.label}
              </Button>
            ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
